/* eslint-disable */
import { createSlice } from '@reduxjs/toolkit';

import { SnackbarTypeEnum } from 'shared/components/feedback/Snackbar/SnackbarTypes.enum';
import { DEFAULT_SNACKBAR_DURATION } from 'utils/constants';

import {
  addUser,
  updateUser,
  resetUserPassword as adminResetUserPassword,
  reInviteUser as adminReInviteUser,
  updateAdminConsult,
  linkOrder as adminLinkOrder,
  unlinkOrder as adminUnlinkOrder,
  createHl7Patient,
  updateHl7Patient,
  createHl7Order,
  updateHl7Order,
} from 'features/admin/store/actionCreators';
import {
  UPDATE_CONSULT_ACTION,
  addNewConsult,
  getConsultById,
  addNewMacro,
  applyMacro,
  linkOrder,
  unlinkOrder,
} from 'features/consults/store/actionCreators';
import { updateProfile } from 'features/profile/store/actionCreators';
import {
  acceptConsultRequest,
  deleteConsultRequest,
} from 'features/customer/store/actionCreators';
import { SuccessActionTypesEnum } from './SuccessActionTypes.enum';

const appStateSlice = createSlice({
  name: 'appState',
  initialState: {
    loading: false,
    successAction: null,
    snackbar: {
      visible: false,
      text: '',
      type: SnackbarTypeEnum.SUCCESS,
      duration: DEFAULT_SNACKBAR_DURATION,
      closable: false,
      requireManualClose: false,
    },
    assets: {},
  },
  reducers: {
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    updateAssets: (state, { payload }) => {
      state.assets = {
        ...state.assets,
        ...payload,
      };
    },
    showSnackbar: (state, { payload }) => {
      const duration =
        payload.duration !== undefined
          ? payload.duration
          : DEFAULT_SNACKBAR_DURATION;

      state.snackbar = {
        ...payload,
        duration,
        visible: true,
      };
    },
    hideSnackbar: (state) => {
      state.snackbar.visible = false;
    },
    removeSuccessAction: (state) => {
      state.successAction = null;
    },
    macroReload: (state) => {
      state.successAction = {
        type: SuccessActionTypesEnum.FORCE_CONSULT_RELOAD,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addUser.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.USER_ADDED,
          payload,
        };
      })
      .addCase(updateUser.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.USER_UPDATED,
          payload,
        };
      })
      .addCase(adminResetUserPassword.fulfilled, (state) => {
        state.successAction = {
          type: SuccessActionTypesEnum.ADMIN_USER_PASSWORD_RESET,
        };
      })
      .addCase(adminReInviteUser.fulfilled, (state) => {
        state.successAction = {
          type: SuccessActionTypesEnum.ADMIN_USER_REINVITED,
        };
      })
      .addCase(updateProfile.fulfilled, (state) => {
        state.successAction = {
          type: SuccessActionTypesEnum.PROFILE_UPDATED,
        };
      })
      .addCase(addNewConsult.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.CONSULT_CREATED,
          payload,
        };
      })
      .addCase(getConsultById.fulfilled, (state, { payload }) => {
        if (payload.forceReload) {
          state.successAction = {
            type: SuccessActionTypesEnum.FORCE_CONSULT_RELOAD,
            payload,
          };
        }
      })
      .addCase(acceptConsultRequest.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.CONSULT_REQUEST_ACCEPTED,
          payload,
        };
      })
      .addCase(deleteConsultRequest.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.CONSULT_REQUEST_DELETED,
          payload,
        };
      })
      .addCase(linkOrder.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.ORDER_LINKED,
          payload,
        };
      })
      .addCase(unlinkOrder.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.ORDER_UNLINKED,
          payload,
        };
      })
      .addCase(adminLinkOrder.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.ADMIN_ORDER_LINKED,
          payload,
        };
      })
      .addCase(adminUnlinkOrder.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.ADMIN_ORDER_UNLINKED,
          payload,
        };
      })
      .addCase(addNewMacro.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.MACRO_CREATED,
          payload,
        };
      })
      .addCase(applyMacro.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.MACRO_APPLIED,
          payload,
        };
      })
      .addCase(updateAdminConsult.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.UPDATED_ADMIN_CONSULT,
          payload,
        };
      })
      .addCase(createHl7Patient.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.HL7_PATIENT_CREATED,
          payload,
        };
      })
      .addCase(updateHl7Patient.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.HL7_PATIENT_UPDATED,
          payload,
        };
      })
      .addCase(createHl7Order.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.HL7_ORDER_CREATED,
          payload,
        };
      })
      .addCase(updateHl7Order.fulfilled, (state, { payload }) => {
        state.successAction = {
          type: SuccessActionTypesEnum.HL7_ORDER_UPDATED,
          payload,
        };
      })
      .addCase('logout/reset', () => {
        state.successAction = {
          type: SuccessActionTypesEnum.LOGOUT_RESET,
        };
      })
      .addMatcher(
        (action) =>
          action.type.endsWith('/pending') &&
          !action.type.startsWith(UPDATE_CONSULT_ACTION),
        (state) => {
          state.loading = true;
        },
      )
      .addMatcher(
        (action) =>
          action.type.endsWith('/fulfilled') ||
          action.type.endsWith('/rejected'),
        (state) => {
          state.loading = false;
        },
      );
  },
});

export const { reducer: appStateReducer, actions: appStateActions } =
  appStateSlice;

export default appStateSlice;
